import React, { useEffect, useContext, useCallback, useState } from 'react';
import { mergeClasses } from '@fluentui/react-components';
import { I18nContext, I18nModel } from '@bingads-webui/copilot-i18n';
import { getParameter } from '@bingads-webui/url-util';
import { TeachingBubble } from '@bingads-webui-campaign-react/popup-components';
import { getConversations } from '@bingads-webui-react/copilot-chat-history-data';
import { useConversationHistoryContext } from './conversation-history-context';

import { ChatHistoryIconsScreenshot } from './media/chat-history-icons';
import { ChatHistorySingleIcon } from './media/chat-history-single-icon';
import { chatHistoryTeachingBubbleStyles } from './styles';
import type {
  ChatHistoryTeachingBubbleContainerProps,
  ChatHistoryTeachingBubbleProps,
} from './types';

export const ChatHistoryTeachingBubbleContainer = ({
  isLargePane,
}: ChatHistoryTeachingBubbleContainerProps) => {
  const i18n = useContext(I18nContext) as I18nModel;
  const classes = chatHistoryTeachingBubbleStyles();


  return (
    <div className={mergeClasses('chat-history-teaching-bubble-container', classes.container)}>
      <div className={classes.image}>
        {isLargePane ? <ChatHistoryIconsScreenshot /> : <ChatHistorySingleIcon />}
      </div>
      <div>
        <p className={mergeClasses('chat-history-bubble-header', classes.header)}>{i18n.getString(_TL_('View your chat history'))}</p>
        <p>{i18n.getString(_TL_('Access your chats to reference past information.'))}</p>
      </div>
    </div>
  );
};

export function ChatHistoryTeachingBubble(props: ChatHistoryTeachingBubbleProps) {
  const i18n = useContext(I18nContext) as I18nModel;
  const isTest = getParameter('testName') !== null || getParameter('ise2etest') !== null;
  const forceShowChatHistoryBubble = getParameter('showChatHistoryBubble') !== null;
  const [visible, setVisible] = useState(false);
  const classes = chatHistoryTeachingBubbleStyles();
  const {
    isLargePane, isPROD, isDemo, cid, uid, logger,
  } = props;

  const closeBubble = useCallback(() => {
    setVisible(false);
  }, []);

  const handleClickOnTarget = useCallback(() => {
    setVisible(false);
  }, []);

  const {
    hasConversations, setHasConversations, productName, getAuthToken,
  } = useConversationHistoryContext();

  const fetchConversations = useCallback(async () => {
    if (!cid || !uid) {
      setHasConversations(false);
      return;
    }
    if (hasConversations) {
      return;
    }

    const storedValue = localStorage.getItem('hasConversations');
    if (storedValue === 'true') {
      setHasConversations(true);
      return;
    }

    try {
      const isXandrProduct = productName === 'xandr_curate' || productName === 'xandr_monetization';
      const authToken = isXandrProduct && getAuthToken ? await getAuthToken() : undefined;

      const result = await getConversations({
        cid,
        uid,
        top: 1,
        skip: 0,
        productName,
        authToken,
      }) as {
        data?: {
          conversations?: unknown[];
        };
      };
      const hasHistory = result?.data?.conversations?.length > 0;
      setHasConversations(hasHistory);
    } catch (error) {
      logger.error('[Chat History] Failed to fetch conversations:', error);
      setHasConversations(false);
    }
  }, [cid, uid, setHasConversations, logger, productName, getAuthToken]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    if (!visible) return undefined;

    // Add click handlers to target elements to close the bubble
    const targetElement1 = document.querySelector('.chat-history-button') as HTMLElement | null;
    const targetElement2 = document.querySelector('.ads-copilot-header .collapsed-menu') as HTMLElement | null;
    const targetElements = [targetElement1, targetElement2].filter((el): el is HTMLElement => el !== null);

    // Add listeners
    targetElements.forEach((el) => {
      el.addEventListener('click', handleClickOnTarget);
    });

    // Return cleanup function
    return function cleanup() {
      targetElements.forEach((el) => {
        el.removeEventListener('click', handleClickOnTarget);
      });
    };
  }, [handleClickOnTarget, visible]);


  useEffect(() => {
    setVisible(((!isTest && isPROD) || forceShowChatHistoryBubble || isDemo) && hasConversations);
  }, [isTest, isPROD, forceShowChatHistoryBubble, isDemo, hasConversations]);

  return visible ? (
    <TeachingBubble
      featureName="chat-history-welcome"
      content={(
        <ChatHistoryTeachingBubbleContainer
          isLargePane={isLargePane}
        />
      )}
      primaryButtonText={i18n.getString(_TL_('Got it'))}
      onDone={closeBubble}
      onClose={closeBubble}
      doNotLayer
      targetElement=".chat-history-button, .ads-copilot-header .collapsed-menu"
      isSecondaryButtonHidden
      className={classes.bubble}
    />
  ) : null;
}
